import { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { Edit2, Trash2, Plus, X, Upload, Image, Video, Loader2, Palette, Ruler, Archive, ArchiveRestore } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import AdminSearchFilter from './AdminSearchFilter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Size limits
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_IMAGES = 10;

// Available categories
const CATEGORIES = [
  'Medical Devices',
  'Pain & Cold Relief',
  'First Aid & Wound Care'
];

// Default color options
const DEFAULT_COLORS = ['Red', 'Blue', 'Green', 'Black', 'White', 'Yellow', 'Pink', 'Purple', 'Orange', 'Brown', 'Gray', 'Navy'];
const DEFAULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL'];

const ProductsManagement = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [archiveFilter, setArchiveFilter] = useState('active');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    stock: '',
    images: [],
    video_url: '',
    category: '',
    is_featured: false,
    // Variant fields
    has_variants: false,
    available_colors: [],
    available_sizes: [],
    variants: []
  });
  
  // UI state for adding colors/sizes
  const [newColor, setNewColor] = useState('');
  const [newSize, setNewSize] = useState('');

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      // Merge backend categories with default categories, remove duplicates
      const allCategories = [...new Set([...CATEGORIES, ...response.data])];
      setCategories(allCategories);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted successfully!');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const toggleArchive = async (product) => {
    const next = !product.is_archived;
    try {
      await axios.put(`${API}/admin/products/${product.id}/archive`, { is_archived: next }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(next ? 'Product archived' : 'Product restored to store');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const archiveAll = async () => {
    if (!window.confirm('Archive ALL active products? They will be hidden from the store but not deleted.')) return;
    try {
      const res = await axios.post(`${API}/admin/products/archive-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.archived_count} products archived`);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to archive products');
    }
  };

  // Filter products
  const filteredProducts = useMemo(() => {
    let result = [...products];

    if (archiveFilter !== 'all') {
      result = result.filter(p => archiveFilter === 'archived' ? p.is_archived : !p.is_archived);
    }
    
    // Search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query)
      );
    }
    
    // Category filter
    if (categoryFilter !== 'all') {
      result = result.filter(p => p.category === categoryFilter);
    }
    
    // Stock filter
    if (stockFilter !== 'all') {
      switch (stockFilter) {
        case 'in-stock':
          result = result.filter(p => (p.stock || 0) > 0);
          break;
        case 'low-stock':
          result = result.filter(p => (p.stock || 0) > 0 && (p.stock || 0) <= 10);
          break;
        case 'out-of-stock':
          result = result.filter(p => (p.stock || 0) === 0);
          break;
      }
    }
    
    // Featured filter
    if (featuredFilter !== 'all') {
      result = result.filter(p => featuredFilter === 'featured' ? p.is_featured : !p.is_featured);
    }
    
    return result;
  }, [products, searchQuery, categoryFilter, stockFilter, featuredFilter, archiveFilter]);

  const clearFilters = () => {
    setSearchQuery('');
    setCategoryFilter('all');
    setStockFilter('all');
    setFeaturedFilter('all');
    setArchiveFilter('active');
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      original_price: '',
      stock: '',
      images: [],
      video_url: '',
      category: '',
      is_featured: false,
      has_variants: false,
      available_colors: [],
      available_sizes: [],
      variants: []
    });
    setEditingProduct(null);
    setShowCustomCategory(false);
    setCustomCategory('');
    setNewColor('');
    setNewSize('');
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    // Check if product category is not in the list (custom category)
    const isCustom = product.category && !categories.includes(product.category);
    setShowCustomCategory(isCustom);
    setCustomCategory(isCustom ? product.category : '');
    
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price?.toString() || '',
      original_price: product.original_price?.toString() || '',
      stock: product.stock?.toString() || '',
      images: product.images || (product.image_url ? [product.image_url] : []),
      video_url: product.video_url || '',
      category: isCustom ? '__custom__' : (product.category || ''),
      is_featured: product.is_featured || false,
      has_variants: product.has_variants || false,
      available_colors: product.available_colors || [],
      available_sizes: product.available_sizes || [],
      variants: product.variants || []
    });
    setShowModal(true);
  };

  const handleCategoryChange = (value) => {
    if (value === '__custom__') {
      setShowCustomCategory(true);
      setFormData({...formData, category: '__custom__'});
    } else {
      setShowCustomCategory(false);
      setCustomCategory('');
      setFormData({...formData, category: value});
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (formData.images.length + files.length > MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    for (const file of files) {
      if (file.size > MAX_IMAGE_SIZE) {
        toast.error(`Image "${file.name}" exceeds 5MB limit`);
        continue;
      }

      if (!file.type.startsWith('image/')) {
        toast.error(`"${file.name}" is not a valid image`);
        continue;
      }

      setUploading(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      try {
        const response = await axios.post(`${API}/upload/image`, formDataUpload, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, response.data.url]
        }));
        toast.success(`Image uploaded successfully`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_VIDEO_SIZE) {
      toast.error('Video exceeds 50MB limit');
      return;
    }

    if (!file.type.startsWith('video/')) {
      toast.error('Invalid video file');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/video`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({
        ...prev,
        video_url: response.data.url
      }));
      toast.success('Video uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload video');
    }
    setUploading(false);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const removeVideo = () => {
    setFormData(prev => ({ ...prev, video_url: '' }));
  };

  const addImageUrl = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      if (formData.images.length >= MAX_IMAGES) {
        toast.error(`Maximum ${MAX_IMAGES} images allowed`);
        return;
      }
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, url]
      }));
    }
  };

  // ===== VARIANT HELPERS =====
  
  const addColor = (color) => {
    if (!color.trim()) return;
    if (formData.available_colors.includes(color.trim())) {
      toast.error('Color already added');
      return;
    }
    setFormData(prev => ({
      ...prev,
      available_colors: [...prev.available_colors, color.trim()]
    }));
    setNewColor('');
    regenerateVariants([...formData.available_colors, color.trim()], formData.available_sizes);
  };

  const removeColor = (color) => {
    const newColors = formData.available_colors.filter(c => c !== color);
    setFormData(prev => ({
      ...prev,
      available_colors: newColors,
      variants: prev.variants.filter(v => v.color !== color)
    }));
  };

  const addSize = (size) => {
    if (!size.trim()) return;
    if (formData.available_sizes.includes(size.trim())) {
      toast.error('Size already added');
      return;
    }
    setFormData(prev => ({
      ...prev,
      available_sizes: [...prev.available_sizes, size.trim()]
    }));
    setNewSize('');
    regenerateVariants(formData.available_colors, [...formData.available_sizes, size.trim()]);
  };

  const removeSize = (size) => {
    const newSizes = formData.available_sizes.filter(s => s !== size);
    setFormData(prev => ({
      ...prev,
      available_sizes: newSizes,
      variants: prev.variants.filter(v => v.size !== size)
    }));
  };

  const regenerateVariants = (colors, sizes) => {
    const basePrice = parseFloat(formData.price) || 0;
    const baseStock = parseInt(formData.stock) || 0;
    
    // Generate all combinations
    const newVariants = [];
    
    if (colors.length > 0 && sizes.length > 0) {
      // Both colors and sizes
      for (const color of colors) {
        for (const size of sizes) {
          // Check if variant already exists
          const existing = formData.variants.find(v => v.color === color && v.size === size);
          newVariants.push(existing || {
            color,
            size,
            price: basePrice,
            stock: Math.floor(baseStock / (colors.length * sizes.length)) || 0,
            sku: ''
          });
        }
      }
    } else if (colors.length > 0) {
      // Only colors
      for (const color of colors) {
        const existing = formData.variants.find(v => v.color === color && !v.size);
        newVariants.push(existing || {
          color,
          size: null,
          price: basePrice,
          stock: Math.floor(baseStock / colors.length) || 0,
          sku: ''
        });
      }
    } else if (sizes.length > 0) {
      // Only sizes
      for (const size of sizes) {
        const existing = formData.variants.find(v => v.size === size && !v.color);
        newVariants.push(existing || {
          color: null,
          size,
          price: basePrice,
          stock: Math.floor(baseStock / sizes.length) || 0,
          sku: ''
        });
      }
    }
    
    setFormData(prev => ({ ...prev, variants: newVariants }));
  };

  const updateVariant = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => 
        i === index ? { ...v, [field]: field === 'price' || field === 'stock' ? parseFloat(value) || 0 : value } : v
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price) {
      toast.error('Name and price are required');
      return;
    }

    // Handle category validation
    const finalCategory = formData.category === '__custom__' ? customCategory.trim() : formData.category;
    
    if (!finalCategory) {
      toast.error('Please select or enter a category');
      return;
    }

    if (formData.images.length === 0) {
      toast.error('Please add at least one product image');
      return;
    }

    // Validate variants if enabled
    if (formData.has_variants && formData.variants.length === 0) {
      toast.error('Please add at least one color or size variant');
      return;
    }

    const productData = {
      name: formData.name,
      description: formData.description,
      price: parseFloat(formData.price),
      original_price: formData.original_price ? parseFloat(formData.original_price) : null,
      stock: parseInt(formData.stock) || 0,
      images: formData.images,
      image_url: formData.images[0],
      video_url: formData.video_url || null,
      category: finalCategory,
      is_featured: formData.is_featured,
      // Variant fields
      has_variants: formData.has_variants,
      available_colors: formData.has_variants ? formData.available_colors : [],
      available_sizes: formData.has_variants ? formData.available_sizes : [],
      variants: formData.has_variants ? formData.variants : []
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated successfully!');
      } else {
        await axios.post(`${API}/products`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product added successfully!');
      }
      setShowModal(false);
      resetForm();
      fetchProducts();
      fetchCategories(); // Refresh categories in case a new one was added
    } catch (error) {
      toast.error(editingProduct ? 'Failed to update product' : 'Failed to add product');
    }
  };

  return (
    <>
      {/* Product Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="product-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter product name"
                  data-testid="product-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Enter product description"
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  rows={3}
                  data-testid="product-description-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">Sale Price ($) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="0.00"
                    data-testid="product-price-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Current selling price</p>
                </div>
                <div>
                  <Label htmlFor="original_price">Original Price ($)</Label>
                  <Input
                    id="original_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.original_price}
                    onChange={(e) => setFormData({...formData, original_price: e.target.value})}
                    placeholder="0.00"
                    data-testid="product-original-price-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Shows as slashed price if higher</p>
                </div>
              </div>
              
              <div>
                <Label htmlFor="stock">Stock {formData.has_variants && "(Base - variants have individual stock)"}</Label>
                <Input
                  id="stock"
                  type="number"
                  min="0"
                  value={formData.stock}
                  onChange={(e) => setFormData({...formData, stock: e.target.value})}
                  placeholder="0"
                  data-testid="product-stock-input"
                />
              </div>

              {/* Featured Product Toggle */}
              <div className="flex items-center gap-3 p-3 border rounded-lg bg-amber-50 border-amber-200">
                <input
                  type="checkbox"
                  id="is_featured"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                  className="h-5 w-5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  data-testid="featured-product-toggle"
                />
                <div>
                  <Label htmlFor="is_featured" className="font-semibold cursor-pointer">Featured Product</Label>
                  <p className="text-xs text-amber-700">Show this product on homepage and featured sections</p>
                </div>
              </div>

              {/* VARIANTS SECTION */}
              <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 text-base font-semibold">
                    <Palette className="h-4 w-4" />
                    Product Variants
                  </Label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.has_variants}
                      onChange={(e) => setFormData({...formData, has_variants: e.target.checked})}
                      className="h-4 w-4 rounded border-gray-300"
                      data-testid="enable-variants-toggle"
                    />
                    <span className="text-sm font-medium">Enable Variants</span>
                  </label>
                </div>

                {formData.has_variants && (
                  <div className="space-y-4 pt-2">
                    {/* Colors */}
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <Palette className="h-4 w-4" /> Colors
                      </Label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.available_colors.map((color) => (
                          <span key={color} className="flex items-center gap-1 px-3 py-1 bg-white border rounded-full text-sm">
                            {color}
                            <button
                              type="button"
                              onClick={() => removeColor(color)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <select
                          value={newColor}
                          onChange={(e) => setNewColor(e.target.value)}
                          className="flex-1 h-9 px-3 border rounded-md text-sm"
                        >
                          <option value="">Select a color...</option>
                          {DEFAULT_COLORS.filter(c => !formData.available_colors.includes(c)).map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                        <Input
                          placeholder="Or type custom..."
                          value={newColor}
                          onChange={(e) => setNewColor(e.target.value)}
                          className="flex-1 h-9"
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addColor(newColor))}
                        />
                        <Button type="button" size="sm" onClick={() => addColor(newColor)} disabled={!newColor}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Sizes */}
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <Ruler className="h-4 w-4" /> Sizes
                      </Label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.available_sizes.map((size) => (
                          <span key={size} className="flex items-center gap-1 px-3 py-1 bg-white border rounded-full text-sm">
                            {size}
                            <button
                              type="button"
                              onClick={() => removeSize(size)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <select
                          value={newSize}
                          onChange={(e) => setNewSize(e.target.value)}
                          className="flex-1 h-9 px-3 border rounded-md text-sm"
                        >
                          <option value="">Select a size...</option>
                          {DEFAULT_SIZES.filter(s => !formData.available_sizes.includes(s)).map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                        <Input
                          placeholder="Or type custom..."
                          value={newSize}
                          onChange={(e) => setNewSize(e.target.value)}
                          className="flex-1 h-9"
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSize(newSize))}
                        />
                        <Button type="button" size="sm" onClick={() => addSize(newSize)} disabled={!newSize}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Variant Combinations */}
                    {formData.variants.length > 0 && (
                      <div>
                        <Label className="mb-2 block">Variant Pricing & Stock ({formData.variants.length} combinations)</Label>
                        <div className="max-h-80 overflow-y-auto space-y-3">
                          {formData.variants.map((variant, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-3 bg-white border rounded text-sm">
                              {/* Variant Image */}
                              <div className="flex-shrink-0">
                                {variant.image_url ? (
                                  <div className="relative group">
                                    <img
                                      src={variant.image_url}
                                      alt={variant.color || 'Variant'}
                                      className="w-16 h-16 object-cover rounded border"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => updateVariant(idx, 'image_url', '')}
                                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <label className="flex flex-col items-center justify-center w-16 h-16 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-gray-400 transition-colors">
                                    <Image className="h-5 w-5 text-gray-400" />
                                    <span className="text-xs text-gray-400 mt-1">Photo</span>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;
                                        if (file.size > MAX_IMAGE_SIZE) {
                                          toast.error('Image exceeds 5MB limit');
                                          return;
                                        }
                                        const formDataUpload = new FormData();
                                        formDataUpload.append('file', file);
                                        try {
                                          const response = await axios.post(`${API}/upload/image`, formDataUpload, {
                                            headers: { 
                                              Authorization: `Bearer ${token}`,
                                              'Content-Type': 'multipart/form-data'
                                            }
                                          });
                                          updateVariant(idx, 'image_url', response.data.url);
                                          toast.success('Variant image uploaded');
                                        } catch (error) {
                                          toast.error('Failed to upload image');
                                        }
                                      }}
                                    />
                                  </label>
                                )}
                              </div>
                              {/* Variant Details */}
                              <div className="flex-1 space-y-2">
                                <div className="font-medium text-base">
                                  {[variant.color, variant.size].filter(Boolean).join(' / ') || 'Variant'}
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="flex items-center gap-1">
                                    <span className="text-gray-500">$</span>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={variant.price}
                                      onChange={(e) => updateVariant(idx, 'price', e.target.value)}
                                      className="w-20 h-8 text-sm"
                                      placeholder="Price"
                                    />
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-gray-500">Stock:</span>
                                    <Input
                                      type="number"
                                      min="0"
                                      value={variant.stock}
                                      onChange={(e) => updateVariant(idx, 'stock', e.target.value)}
                                      className="w-16 h-8 text-sm"
                                      placeholder="Stock"
                                    />
                                  </div>
                                  <Input
                                    value={variant.sku || ''}
                                    onChange={(e) => updateVariant(idx, 'sku', e.target.value)}
                                    className="w-24 h-8 text-sm"
                                    placeholder="SKU"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div>
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full h-10 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  data-testid="product-category-input"
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  <option value="__custom__">+ Create New Category</option>
                </select>
                
                {showCustomCategory && (
                  <Input
                    className="mt-2"
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    placeholder="Enter new category name"
                    data-testid="custom-category-input"
                  />
                )}
              </div>

              {/* Multiple Images Section */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Image className="h-4 w-4" />
                    Product Images ({formData.images.length}/{MAX_IMAGES})
                  </Label>
                  <span className="text-xs text-gray-500">Max 5MB each</span>
                </div>
                
                {/* Image Preview Grid */}
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {formData.images.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img}
                          alt={`Product ${index + 1}`}
                          className="w-full h-20 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                        {index === 0 && (
                          <span className="absolute bottom-0 left-0 bg-black text-white text-xs px-1 rounded-tr">Main</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload Buttons */}
                <div className="flex gap-2">
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="image-upload-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploading || formData.images.length >= MAX_IMAGES}
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                    Upload Images
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addImageUrl}
                    disabled={formData.images.length >= MAX_IMAGES}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add URL
                  </Button>
                </div>
              </div>

              {/* Video Section */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Video className="h-4 w-4" />
                    Product Video (1 max)
                  </Label>
                  <span className="text-xs text-gray-500">Max 50MB</span>
                </div>
                
                {formData.video_url ? (
                  <div className="relative">
                    <video
                      src={formData.video_url}
                      className="w-full h-32 object-cover rounded border"
                      controls
                    />
                    <button
                      type="button"
                      onClick={removeVideo}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="hidden"
                      data-testid="video-upload-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => videoInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                      Upload Video
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const url = prompt('Enter video URL:');
                        if (url) setFormData(prev => ({ ...prev, video_url: url }));
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add URL
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-black text-white hover:bg-zinc-800" data-testid="product-submit-btn">
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>All Products ({filteredProducts.length})</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={archiveAll} data-testid="archive-all-products-button">
                <Archive className="h-4 w-4 mr-2" />
                Archive All
              </Button>
              <Button onClick={openAddModal} data-testid="add-product-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
      <CardContent>
        {/* Search and Filters */}
        <AdminSearchFilter
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search products by name, description, category..."
          filters={[
            {
              key: 'archive',
              label: 'Status',
              value: archiveFilter,
              onChange: setArchiveFilter,
              options: [
                { value: 'active', label: 'Live in Store' },
                { value: 'archived', label: 'Archived' },
                { value: 'all', label: 'All Statuses' }
              ]
            },
            {
              key: 'category',
              label: 'Category',
              value: categoryFilter,
              onChange: setCategoryFilter,
              options: [
                { value: 'all', label: 'All Categories' },
                ...categories.map(c => ({ value: c, label: c }))
              ]
            },
            {
              key: 'stock',
              label: 'Stock',
              value: stockFilter,
              onChange: setStockFilter,
              options: [
                { value: 'all', label: 'All Stock' },
                { value: 'in-stock', label: 'In Stock' },
                { value: 'low-stock', label: 'Low Stock (≤10)' },
                { value: 'out-of-stock', label: 'Out of Stock' }
              ]
            },
            {
              key: 'featured',
              label: 'Featured',
              value: featuredFilter,
              onChange: setFeaturedFilter,
              options: [
                { value: 'all', label: 'All Products' },
                { value: 'featured', label: 'Featured Only' },
                { value: 'not-featured', label: 'Not Featured' }
              ]
            }
          ]}
          onClearFilters={clearFilters}
          resultCount={filteredProducts.length}
          resultLabel="products"
        />

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No products found matching your filters
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <div key={product.id} className={`border rounded-lg p-4 ${product.is_archived ? 'opacity-60 bg-gray-50' : ''}`} data-testid={`product-${product.id}`}>
                <div className="relative">
                  <img
                    src={product.images?.[0] || product.image_url}
                    alt={product.name}
                    className="w-full h-48 object-cover mb-2 bg-gray-100 rounded"
                  />
                  {product.images?.length > 1 && (
                    <span className="absolute bottom-3 right-1 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                      +{product.images.length - 1} more
                    </span>
                  )}
                  {product.video_url && (
                    <span className="absolute top-1 right-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
                      <Video className="h-3 w-3" /> Video
                    </span>
                  )}
                  {product.is_featured && (
                    <span className="absolute top-8 right-1 bg-amber-500 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
                      ★ Featured
                    </span>
                  )}
                  {product.is_archived && (
                    <span className="absolute bottom-3 left-1 bg-gray-700 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1" data-testid={`archived-badge-${product.id}`}>
                      <Archive className="h-3 w-3" /> Archived
                    </span>
                  )}
                  {product.has_variants && (
                    <span className="absolute top-1 left-1 bg-purple-500 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
                      <Palette className="h-3 w-3" /> Variants
                    </span>
                  )}
                </div>
                <h3 className="font-heading font-semibold mb-2 line-clamp-2">{product.name}</h3>
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">{product.description}</p>
                <div className="mb-2">
                  {product.original_price && product.original_price > product.price ? (
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-red-600">${product.price.toFixed(2)}</span>
                      <span className="text-gray-400 line-through text-sm">${product.original_price.toFixed(2)}</span>
                      <span className="bg-red-100 text-red-600 text-xs px-1.5 py-0.5 rounded">
                        {Math.round((1 - product.price / product.original_price) * 100)}% OFF
                      </span>
                    </div>
                  ) : (
                    <span className="font-bold">${product.price.toFixed(2)}</span>
                  )}
                  {product.has_variants && product.variants?.length > 0 && (
                    <span className="text-xs text-gray-500 ml-2">
                      ({product.variants.length} variants)
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600 mb-2">
                  {product.has_variants ? (
                    <div className="flex flex-wrap gap-1">
                      {product.available_colors?.length > 0 && (
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          {product.available_colors.length} colors
                        </span>
                      )}
                      {product.available_sizes?.length > 0 && (
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          {product.available_sizes.length} sizes
                        </span>
                      )}
                    </div>
                  ) : (
                    <span>Stock: {product.stock}</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditModal(product)} data-testid={`edit-product-${product.id}`}>
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleArchive(product)}
                    title={product.is_archived ? 'Restore to store' : 'Archive'}
                    data-testid={`archive-toggle-${product.id}`}
                  >
                    {product.is_archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDelete(product.id)}
                    data-testid={`delete-product-${product.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
};

export default ProductsManagement;
